import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode, type UIEvent } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppearance } from '@/hooks/useAppearance';
import { PartitaVivaV3, RigaTorneoV3, serveUscitaV3, showsTabBarV3, TabBarV3, TestataV3, UscitaV3, useV3 } from '@/lib/v3';
import { useBackButton } from '@/hooks/useBackButton';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';
import { useProfileSync } from '@/hooks/useProfileSync';
import { useEngagementEngine } from '@/hooks/useEngagementEngine';
import { usePendingInvite } from '@/hooks/usePendingInvite';
import { useCloudPref, CloudPref } from '@/hooks/useCloudPref';
import { useLinguaDalCloud } from '@/hooks/useLinguaDalCloud';
import { useTempoRealeQsocials } from '@/hooks/useTempoRealeQsocials';
import { useTempoRealeLimulo } from '@/hooks/useTempoRealeLimulo';
import { useOspite, useRitornoDopoAccesso, useSceltaOspite } from '@/hooks/useOspite';
import { rottaApertaAllOspite } from '@/lib/ospite';
import { GuscioProtetto } from '@/components/minigiochi/GuscioProtetto';
import { GuscioFeedbackProtetto } from '@/components/feedback/GuscioFeedbackProtetto';
import { usePrimaManoSegnale } from '@/lib/telemetria/primaManoBridge';
import { isDevBypassActive } from '@/lib/devBypass';
import { decidiCompressione, type ScrollVisto } from '@/lib/compressioneScroll';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setTourActive } from '@/store/core.slice';
import { setBannerCompact } from '@/store/settings.slice';
import { cn } from '@/lib/cn';
import { AdminGate } from '@/components/common/AdminGate';
import { Login } from '@/components/common/Login';
import { RichiedeAccesso } from '@/components/common/RichiedeAccesso';
import { Benvenuto } from '@/components/common/Benvenuto';
import { Onboarding, type OnboardingCompleteOpts } from '@/components/onboarding/Onboarding';
import { MainCtaProvider } from '@/contexts/MainCtaContext';
import { AppBannerProvider } from '@/contexts/AppBannerContext';
import { ActiveMatchProvider } from '@/contexts/ActiveMatchContext';
import { LandingNoticeProvider } from '@/contexts/LandingNoticeContext';
import { ResumableMatchProvider } from '@/contexts/ResumableMatchContext';
import { UscitaTavoloProvider } from '@/contexts/UscitaTavoloContext';
import { CodeTorneiProvider } from '@/contexts/CodeTorneiContext';
import { SfideBarraProvider } from '@/contexts/SfideBarraContext';
import { AppBanner } from './AppBanner';
import { EngagementNudge } from './EngagementNudge';
import { MainCta } from './MainCta';
import { TabBar } from './TabBar';
import { normalizzaPercorso, showsTabBar, tabIndex } from './tabBarRoutes';
import { ActiveMatchPill } from './ActiveMatchPill';
import { AzionePersistente, RigaAzione, type PropsRigaAzione } from './AzionePersistente';
import { OsservatoreEsiti } from './ModaleEsito';
import { BARRA_SCHEDA_ID } from './barraScheda';
import { LandingNoticeModal } from './LandingNoticeModal';
import { Backpack } from '@/components/profile/Backpack';
import { PackTab } from '@/components/profile/PackTab';

// L'ordine delle schede, il loro indice, la voce accesa e la regola della TabBar (barra su OGNI schermata della shell
// tranne l'elenco delle eccezioni, Luca 04/10, I-382) stanno in `tabBarRoutes.ts`, dove un test li esercita.
const PROFILE_PATH = '/profile';

/**
 * Le schermate che si prendono tutto lo schermo, senza la chrome sopra.
 *
 * Leggere un articolo è l'unica cosa che si fa a lungo e a testa bassa: banner,
 * livello, nickname e valute lì non dicono nulla di utile e costano un quinto
 * dell'altezza a ogni schermata di testo. La barra in basso resta, perché
 * uscire dev'essere sempre possibile.
 *
 * `/news/` con la barra finale, non `startsWith('/news')`: l'ELENCO delle news
 * è una scheda come le altre e la chrome ci sta bene. Sparisce solo dentro un
 * articolo.
 */
/**
 * Le rotte dove la chrome d'app (`<AppBanner />`) NON si monta.
 *
 * 🔴 LA SECONDA VOCE E' UNA DECISIONE DELL'OPERATORE, non un'ottimizzazione. Luca, 12/09,
 * testuale: «**Vedo ancora banner in testa alla pagina (quello che compare in hub), puoi
 * rimuoverlo e sostituirlo con qualche altro testo**» — guardando la schermata di una partita di
 * backgammon, dove il banner occupava la cima di uno schermo la cui regola e' *«il tabellone e' il
 * piu' grande possibile e non si scorre mai»*.
 *
 * ⚠️ E la cosa da sapere prima di allargare questo elenco: **il banner e' un CONTENITORE, e dentro
 * ci sono livello e valute** — messe li' apposta nel restyle del 06/09 perche' comparissero su ogni
 * schermata. ⭐ *Togliere un contenitore e' una decisione sul contenuto.* Qui si regge perche' la
 * rimozione vale **una rotta sola**: livello e valute restano ovunque altrove. Chi aggiunge una
 * rotta a questo elenco sta decidendo anche su quelle due cose, e deve saperlo.
 *
 * ⛔ **Confronto esatto sul prefisso della PARTITA** (`/backgammon/match/`), non su
 * `/backgammon`: la lobby e la classifica sono schermate normali e il banner ci sta bene.
 */
function isImmersive(pathname: string): boolean {
  return (
    pathname.startsWith('/news/') ||
    pathname.startsWith('/backgammon/match/') ||
    pathname.startsWith('/chess/match/') ||
    // il TAVOLO del burraco (Stefano 17/10, d'accordo Luca): quello vero (`/burraco/tavolo/:id`) e la mano di prova delle fixture
    // (`/burraco/tavolo`, dove porta il tutorial), che e lo stesso tavolo e deve avere lo stesso spazio. La Hub del burraco, la verifica e
    // l'audit restano schermate normali, col banner
    pathname === '/burraco/tavolo' ||
    pathname.startsWith('/burraco/tavolo/')
  );
}


/**
 * La testata della shell: `AppBanner` di oggi, o con la v3 accesa il pezzo nuovo (`TestataV3`, fase 2). UN punto in cui `AppShell` sceglie fra i due con il
 * predicato `useV3()`; nessun ramo v3 dentro `AppBanner`. Il fallback del `Suspense` e' la testata di oggi: finche' il pezzo della v3 non arriva non c'e' un
 * buco di layout. Dove si monta (`!ospite && !isImmersive(...)`) resta nel JSX di `AppShell`, perche' i cancelli (`verify-ospite`, `verify-match-layout`) lo leggono li'.
 */
function TestataDellaShell({ v3 }: { v3: boolean }) {
  if (v3 && TestataV3) {
    return (
      <Suspense fallback={<AppBanner />}>
        <TestataV3 />
      </Suspense>
    );
  }
  return <AppBanner />;
}

/**
 * La barra in basso della shell: `TabBar` di oggi, o con la v3 accesa `TabBarV3` (a quattro voci, fase 2). UN punto in cui `AppShell` sceglie fra i due con il
 * predicato `useV3()`; nessun ramo v3 dentro `TabBar`. Il fallback del `Suspense` e' la barra di oggi (nessun buco finche' il pezzo non arriva). Dove si monta
 * (`{tabBarVisible && <BarraDellaShell … />}`) resta nel JSX di `AppShell`, perche' i cancelli (`verify-minigiochi`) lo leggono li'.
 */
function BarraDellaShell({ v3 }: { v3: boolean }) {
  if (v3 && TabBarV3) {
    return (
      <Suspense fallback={<TabBar />}>
        <TabBarV3 />
      </Suspense>
    );
  }
  return <TabBar />;
}

/**
 * L'uscita delle schermate senza barra della v3 (PR 6, ADR-0242 «Conseguenze»): `UscitaV3`, o niente. Mai un fallback: finche' il pezzo non arriva non c'e' un buco, c'e'
 * semplicemente la schermata un attimo piu' in alto. Dove si monta (`{serveUscitaV3(...) && <UscitaDellaShell v3={v3} />}`) resta nel JSX di `AppShell`.
 */
function UscitaDellaShell({ v3 }: { v3: boolean }) {
  if (v3 && UscitaV3) {
    return (
      <Suspense fallback={null}>
        <UscitaV3 />
      </Suspense>
    );
  }
  return null;
}

/**
 * La riga della barra del torneo con la v3 accesa (PR 9b): `RigaTorneoV3`, caricata a richiesta. Il fallback del `Suspense` e' la riga di oggi (nessun buco finche' il pezzo non arriva).
 * Un componente di MODULO, con identita' stabile: `AzionePersistente` lo riceve come `Riga` e un'identita' nuova a ogni render la rimonterebbe.
 */
function RigaDellaShell(props: PropsRigaAzione) {
  return <Suspense fallback={<RigaAzione {...props} />}>{RigaTorneoV3 ? <RigaTorneoV3 {...props} /> : null}</Suspense>;
}

/**
 * L'azione persistente dei tornei: `AzionePersistente` di oggi, o con la v3 accesa la stessa con la riga nuova (`RigaTorneoV3`, fase 2). UN punto in cui `AppShell` sceglie con il predicato
 * `useV3()`; nessun ramo v3 dentro `AzionePersistente` (riceve la riga come prop, la logica e' la stessa). Dove si monta (dentro `SfideBarraProvider`, sopra `MainCta`) resta nel JSX di `AppShell`.
 */
function AzioneDellaShell({ v3, insetBottom }: { v3: boolean; insetBottom: boolean }) {
  if (v3 && RigaTorneoV3) {
    return <AzionePersistente insetBottom={insetBottom} Riga={RigaDellaShell} />;
  }
  return <AzionePersistente insetBottom={insetBottom} />;
}

/**
 * La barra del torneo e la partita viva sul PROPRIO Profilo (PR 9c): con la v3 accesa stanno dentro `pack-scope` e prendono il cuoio, come oggi la `ActiveMatchPill` (un teal sopra il marrone e` il guasto
 * gia' visto). `display: contents`: non cambia l'impaginazione. A v3 spenta e` un frammento: l'albero e` quello di oggi, pixel e DOM.
 */
function CuoioDellaShell({ v3, profilo, children }: { v3: boolean; profilo: boolean; children: ReactNode }) {
  return v3 ? <div className={cn('contents', profilo && 'pack-scope')}>{children}</div> : <>{children}</>;
}

/**
 * Top-level layout: drives Logto auth, wires the BackButton, and renders the
 * active route inside a flex column with a bottom TabBar. While auth is
 * resolving we render a minimal splash; if the user is not authenticated we
 * render the <Login /> CTA instead of the route tree.
 */
export function AppShell() {
  const { isAuthenticated, isLoading } = useLogtoAuth();
  // L'OSPITE (`lib/ospite.ts`, I-193): interruttore acceso e nessuna sessione ⇒ si entra lo stesso,
  // e le schermate che chiedono un account mostrano «Accedi» al posto del contenuto (piu' giu').
  // A interruttore spento e' sempre `false`: il cancello resta `<Login />`, identico a prima.
  // ⚠️ `useOspite`, non `!isAuthenticated` qui: e' lo STESSO predicato della guardia di rete
  // (`haSessione`, bridge O SDK), cosi' cio' che si vede e cio' che esce non divergono mai.
  const ospite = useOspite();
  // C1 di Luca (25/09 ~23:2xZ, «C1 (b)»): al primo avvio da ospite, il BENVENUTO con due scelte;
  // «Entra come ospite» si ricorda e da li' in poi si entra dritti (v. il ramo sotto <Login />).
  const { scelto: sceltoOspite } = useSceltaOspite();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Il segnale anonimo della prima mano di QBridge (ADR-0236 §7): l'UNICO punto di chiamata, fuori da K9a.
  usePrimaManoSegnale();

  /**
   * Scorrendo, il banner si comprime; tornando in cima si riespande.
   *
   * La compressione parte al PRIMO spostamento verso il basso, non dopo un tratto (Luca, I-386,
   * 04/10: «c'e' un micro movimento della lista prima della minimizzazione»), e l'isteresi sta
   * nella DIREZIONE e non in due soglie: la decisione e le sue ragioni, con la misura, sono in
   * `lib/compressioneScroll.ts`. Qui si tiene solo lo stato di ogni scroller.
   */
  const bannerCompact = useAppSelector((s) => s.settings.bannerCompact);
  const scrollVisto = useRef(new WeakMap<HTMLElement, ScrollVisto>());
  const onAnyScroll = useCallback(
    (e: UIEvent<HTMLElement>) => {
      const el = e.target as HTMLElement;
      const y = el.scrollTop;
      const visto = scrollVisto.current.get(el) ?? { y: 0, picco: 0 };
      scrollVisto.current.set(el, visto);

      /**
       * Non comprimere se il contenuto e' appena piu' alto dello schermo.
       *
       * Comprimendo si libera spazio — oggi solo sulla Hub, ~138 px: e' la
       * griglia delle sezioni che si chiude (`HubSections`). La testata no:
       * dalla striscia (layout-tma-orcb, 02/10) resta 80 px anche compressa,
       * quindi fuori dalla Hub comprimere non libera niente. [Misura del
       * 02/10 13:11Z, l'altezza dei contenitori che scorrono prima e dopo
       * `misura-testata.mjs --compatto`, a 360 e 390: prima della
       * striscia la Hub liberava ~180 px, 43-46 di testata e 138 di griglia;
       * questa riga diceva «~150px».] Su un contenuto che sfora di poco,
       * quello spazio basta a farlo entrare tutto, il browser azzera lo scroll
       * perche' non c'e' piu' nulla da scorrere, l'azzeramento riespande la
       * chrome e il contenuto torna a sforare. Il risultato e' un rimbalzo a
       * ogni tentativo di scorrere — visibile per esempio nelle Statistiche di
       * Battaglie, piu' corte della lista battaglie.
       *
       * La soglia sta sopra lo spazio recuperabile: se c'e' meno di tanto da
       * scorrere, comprimere non serve comunque — la chrome non e' d'intralcio
       * su una schermata che quasi ci sta.
       */
      const scorribile = el.scrollHeight - el.clientHeight;
      const decisione = decidiCompressione(visto, y, bannerCompact, scorribile);
      if (decisione === 'comprimi') dispatch(setBannerCompact(true));
      else if (decisione === 'riapri') dispatch(setBannerCompact(false));
    },
    [bannerCompact, dispatch],
  );

  /**
   * Il contenuto entra scivolando dal lato da cui arrivi.
   *
   * La classe si toglie, si forza un reflow e si rimette: ri-assegnare la stessa
   * animazione senza quel passaggio non la fa ripartire, e tornando due volte
   * sulla stessa scheda la seconda resterebbe ferma.
   *
   * Il contenitore NON viene rimontato (niente `key` sul percorso): rimontarlo
   * azzererebbe lo scorrimento a ogni navigazione, compreso il ritorno da un
   * dettaglio a una lista, dove si perderebbe il punto in cui si era.
   */
  const location = useLocation();
  const mainRef = useRef<HTMLElement | null>(null);
  const prevTab = useRef(tabIndex(location.pathname));
  // La v3 (fase 2): i componenti della chrome nuova li sceglie QUESTO file con UN predicato, `useV3()`, e mai con un ramo v3 dentro
  // `AppBanner`/`TabBar`. Spenta, la chrome e' quella di sempre. Prima di `tabBarVisible`: anche la REGOLA della barra ha il suo ramo v3.
  const v3 = useV3();
  const tabBarVisible = v3 && showsTabBarV3 ? showsTabBarV3(location.pathname) : showsTabBar(location.pathname);

  /**
   * Lo Zaino è CHROME, non contenuto del Profilo.
   *
   * Sta qui e non dentro la rotta perché la sua linguetta pende dalla barra di
   * navigazione, che vive qui: renderla dal Profilo avrebbe richiesto di
   * ancorarla in assoluto a un'altezza che dipende dall'area di sicurezza —
   * cioè indovinare da fuori una misura che questo file conosce.
   *
   * Esiste solo sul Profilo. È la stessa consapevolezza che questo file ha già
   * di quella rotta (l'animazione d'ingresso la salta, vedi sopra).
   */
  // Normalizzato (I-382): `/profile/` mostra il Profilo, e senza questo perderebbe zaino e texture. Solo il percorso
  // ESATTO: `/profile/:id` e' il profilo di un altro giocatore, non lo zaino.
  const onProfile = normalizzaPercorso(location.pathname) === PROFILE_PATH;
  const [packOpen, setPackOpen] = useState(false);
  const { t } = useTranslation('general');

  // Uscendo dal Profilo lo zaino si chiude. Senza, tornandoci lo si ritrova
  // aperto senza averlo aperto — uno stato senza causa visibile.
  useEffect(() => {
    if (!onProfile) setPackOpen(false);
  }, [onProfile]);

  useEffect(() => {
    const el = mainRef.current;
    const to = tabIndex(location.pathname);
    const from = prevTab.current;
    prevTab.current = to;
    if (!el) return;

    // Verso il Profilo niente: il banner che si stringe È già l'animazione di
    // quella transizione, e due movimenti per un gesto solo si disturbano.
    if (normalizzaPercorso(location.pathname) === PROFILE_PATH) return;

    // Fra due schede c'è un verso; da o verso un dettaglio no — lì lo
    // scivolamento mentirebbe su uno spostamento laterale che non è avvenuto,
    // quindi resta il solo dissolvimento.
    const cls =
      from >= 0 && to >= 0 ? (to < from ? 'screen-in-left' : 'screen-in-right') : 'screen-in';

    el.classList.remove('screen-in-left', 'screen-in-right', 'screen-in');
    void el.offsetWidth;
    el.classList.add(cls);
  }, [location.pathname]);

  const {
    value: onboardingSeen,
    ready: onboardingReady,
    set: setOnboardingSeen,
  } = useCloudPref(CloudPref.OnboardingSeen);
  useLinguaDalCloud();

  const handleOnboardingComplete = useCallback(
    (opts?: OnboardingCompleteOpts) => {
      // Flag first: the wizard must never reappear, even mid-navigation.
      setOnboardingSeen('1');
      if (opts?.startTour) dispatch(setTourActive(true));
      if (opts?.navigateTo) navigate(opts.navigateTo);
      else if (opts?.startTour) navigate('/');
    },
    [setOnboardingSeen, dispatch, navigate],
  );
  // Theme engine: applies the active palette (CSS vars + Telegram chrome) and
  // persists pref changes. Mounted here so it runs for both Login and routes.
  useAppearance();
  useBackButton();
  // QSocials presence heartbeat — 30s tick while focused, /offline on
  // blur/unload. No-op until the user is authenticated (the hook checks
  // bridgeAuthenticated internally).
  usePresenceHeartbeat();
  // QSocials tempo reale — UN collegamento a `qsocials-ws` per app, qui e non per pagina.
  // No-op con `VITE_QSOCIALS_WS_URL` vuota o senza sessione; il controllo sta dentro l'hook.
  useTempoRealeQsocials();
  // Il tempo reale di Limulo (kb#275): il campanello delle azioni. Spento fuori da `.env.staging`.
  useTempoRealeLimulo();
  // QSocials profile sync — pubblica il nome pubblico + l'avatar su qsocials una volta
  // per sessione, così le liste amici/follow mostrano una maniglia invece di un `sub`.
  // No-op finché non si è autenticati; il controllo sta dentro l'hook.
  //
  // 🔻 Corretto il 2026-09-10: la riga diceva «push **Telegram** displayName». È fuorviante
  // e nel verso che tranquillizza. [M] `useProfileSync.ts:47-48` pubblica **la maniglia
  // QLASH** (`auth.user.username`); lo `@username` Telegram è **solo il ripiego**, e il
  // nome/cognome Telegram non si pubblica MAI — è scritto nel blocco in testa a quell'hook.
  //
  // ⚠️ E oggi la differenza conta: con `featureLimuloEnabled` acceso, `auth.user.username`
  // NON viene più dal monolite ma da `GET /v1/me` (`lib/limulo/identity.ts:76`:
  // `username: orUndefined(me.user.name)`). ⇒ **Ciò che finisce qui, e diventa la chiave di
  // ricerca e delle liste amici, è quello che manda Limulo** — e cosa contenga `me.user.name`
  // per un utente senza nickname è, al 2026-09-10, **una lettura che nessuno ha ancora
  // fatto dopo il cambio di contratto dell'08/09** (ADR-0125, Limulo #48/#49).
  // ⛔ Questo hook è montato **senza nessun gate**: cambiare quella sorgente cambia ciò che
  // viene pubblicato per ogni utente autenticato, non per un sottoinsieme.
  useProfileSync();
  // In-app engagement: derives actionable prompts (register/check-in/play) from
  // the user's tournaments and surfaces them as a nudge + inbox. No-op until
  // authenticated (the hook checks bridgeAuthenticated internally).
  useEngagementEngine();
  // L'altra meta' del deep link: il payload messo da parte al bootstrap
  // (`main.tsx` → `stashPendingStartParam`) diventa una navigazione appena
  // l'app e' USABILE. ⚠️ «Usabile» non e' «autenticato»: un ospite nuovo passa
  // dall'onboarding, e portarlo sull'invito mentre il wizard e' a schermo
  // significherebbe consumare il payload nel momento in cui non puo' agirci.
  // E' per questo che lo stash dura piu' dell'invito — v. `lib/startParam.ts`.
  // Chi era ospite e ha toccato «Accedi» torna sulla schermata da cui e' partito, non sulla Hub
  // (il login dal telefono riapre la Mini App da `/`). PRIMA dell'invito: se c'e' un invito o una
  // sfida in attesa, il ritorno si scarta dentro l'hook e vince quel payload.
  useRitornoDopoAccesso(isAuthenticated && onboardingReady && onboardingSeen === '1');
  usePendingInvite(isAuthenticated && onboardingReady && onboardingSeen === '1');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text">
        <p className="text-fg-muted">{t('shell.loading')}</p>
      </div>
    );
  }

  // Dev escape hatch: when running locally outside of Telegram (or with
  // Logto staging redirect URI not yet whitelisted), append `?dev=1` to the
  // URL to skip the auth gate and preview unauthenticated routes. Authenticated
  // RTK Query calls will fail with 401 — that's expected.
  // Latched for the tab (see lib/devBypass): react-router navigations drop the
  // query string, so re-reading location.search here killed the bypass at the
  // first SPA navigation. Compiles to `false` in production builds.
  //
  // CriGio era arrivato alla stessa diagnosi e alla stessa cura, ma scritta qui
  // dentro: la scrittura in sessionStorage finiva nel corpo del render (effetto
  // collaterale durante il render, e senza try/catch: in navigazione privata
  // `sessionStorage` puo' lanciare). Il modulo la tiene fuori e degrada.
  const devBypass = isDevBypassActive();

  if (!isAuthenticated && !devBypass && !ospite) {
    return <Login />;
  }

  // Il benvenuto dell'ospite prende il posto di <Login /> SOLO con l'interruttore acceso (`ospite`
  // e' sempre `false` a interruttore spento, quindi in produzione questo ramo non c'e').
  if (ospite && !sceltoOspite && !devBypass) {
    return <Benvenuto />;
  }

  // First-run flag still hydrating from CloudStorage (fresh device, empty
  // local cache): hold the splash instead of flashing Hub-then-wizard.
  if (isAuthenticated && !onboardingReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text">
        <p className="text-fg-muted">{t('shell.loading')}</p>
      </div>
    );
  }

  const needsOnboarding = isAuthenticated && onboardingSeen !== '1';

  return (
    // ADR-0115 — il provider sta SOPRA l'<Outlet />, non dentro una rotta: un
    // avviso di atterraggio deve sopravvivere alla rotta che lo alza, perche' il
    // senso della decisione e' che chi fallisce se ne VADA verso una superficie
    // viva e il messaggio resti. Dentro la rotta, andarsene lo smonterebbe.
    <LandingNoticeProvider>
    <ActiveMatchProvider>
      {/* La conferma «Lasciare il tavolo?» per la barra E per il tasto indietro (I-392, I-402): sta DENTRO
          `ActiveMatchProvider` perche' legge la partita viva, e per l'indietro si registra come freno
          (`lib/livelliIndietro.ts`), perche' `useBackButton` sta qui sopra, fuori dal provider. */}
      <UscitaTavoloProvider>
      {/* 🔴 ALL'AVVIO, non nella lobby: chi apre la Hub e non entra in backgammon
          aveva una partita viva e non lo sapeva. Sta DENTRO il ramo autenticato
          (dopo il return su <Login />), quindi non parte per chi non ha sessione.
          ⚠️ Con l'OSPITE acceso questo ramo si monta anche senza sessione: il provider
          resta inerte perche' la sua prima lettura (`/me` di QGammon) la ferma la guardia
          di rete (`services/guardiaOspite.ts`), e senza `me` il `fetch` diretto non parte.
          Il costo di questa altitudine e le opzioni per governarlo sono
          dichiarati in ResumableMatchContext.tsx. */}
      <ResumableMatchProvider>
      {/* Il biglietto di coda di qTournaments (kb#282) vive nell'APP, non nella scheda «Ranked»:
          cambiare scheda non deve far uscire dalla coda. A flag spento e' assente. Da solo chiede
          al server una cosa: lista delle code e catalogo, UNA volta all'avvio con sessione, per
          riprendere un'attesa (v. `CodeTorneiContext`); il resto parte quando si entra in coda. */}
      <CodeTorneiProvider>
      <MainCtaProvider>
        <AppBannerProvider>
          {/* h-full, not h-[100dvh]: #root already owns the height question and
          answers it with Telegram's own `--tg-viewport-stable-height` (see
          globals.css). 100dvh measured the engine window instead — taller than
          the visible WebView — so the shell overflowed and the whole page
          scrolled, TabBar and all. Long routes (Tournaments / News) get scroll
          via <main>'s overflow-y-auto; nothing above it may scroll. */}
          {/* Nessun fondo qui di proposito: la texture dell'app sta su <body> (vedi
          globals.css) e un `bg-bg` opaco su questo contenitore la coprirebbe
          interamente. Lo shell deve essere trasparente e lasciarla passare. */}
          <div className="flex h-full flex-col overflow-hidden text-text">
            {/* AdminGate sits AFTER the auth gate above: unauthenticated users
            still see <Login /> first; only authenticated users hit the admin
            check. When VITE_ADMIN_GATE_ENABLED=false (PROD) it's a pass-through. */}
            <AdminGate>
              {needsOnboarding ? (
                <>
                  <main className="min-h-0 flex-1 overflow-y-auto">
                    <Onboarding onComplete={handleOnboardingComplete} />
                  </main>
                  <MainCta />
                </>
              ) : (
                <>
                  {/* La texture di fondo tinta di cuoio, solo sul Profilo.
                      Sta PRIMA di tutto: nello strato dei posizionati vince chi
                      viene dopo nel documento, quindi mettendolo in testa ogni
                      cosa gli passa davanti senza bisogno di z-index. */}
                  {onProfile && <div aria-hidden className="pack-tint" />}

                  {/* Constant chrome, outside the scroller: same banner, same spot,
                  every screen. Routes speak through its slots (BannerSlots.tsx)
                  instead of rendering their own.
                  Tranne dentro un articolo: vedi `isImmersive`. */}
                  {/* Da OSPITE il banner non si monta: e' lo stendardo PERSONALE (sfondo scelto, nickname,
                      avatar, livello, valute) e ogni sua fonte chiede un account — montato, era una
                      cornice vuota (rilievo di tma-orcb sul servito, 25/09). */}
                  {!ospite && !isImmersive(location.pathname) && <TestataDellaShell v3={v3} />}
                  {v3 && serveUscitaV3?.(location.pathname) && <UscitaDellaShell v3={v3} />}
                  {/* La compressione del banner e' guidata da QUI, non dalla Hub:
                  vale su ogni schermata, non solo dove esiste la griglia delle
                  sezioni. In CATTURA perche' `scroll` non risale l'albero e
                  quasi tutte le rotte portano il proprio scroller interno —
                  un handler normale non vedrebbe nulla. */}
                  <main
                    ref={mainRef}
                    className={cn(
                      // `relative`: il velo del Profilo sta nello strato dei
                      // posizionati, e senza questo lo scorrevole gli finirebbe
                      // sotto. Non sposta nulla — main non ha coordinate.
                      'relative min-h-0 flex-1 overflow-y-auto',
                      // Senza la barra sotto, l'ultima riga di contenuto
                      // finirebbe dietro la barra gesti del sistema: lo spazio
                      // che prima dava la barra lo dà ora lo scorrevole.
                      !tabBarVisible &&
                        'pb-[max(env(safe-area-inset-bottom,0px),var(--tg-viewport-safe-area-inset-bottom,0px))]',
                    )}
                    onScrollCapture={onAnyScroll}
                  >
                    {/* Da ospite si vedono SOLO le rotte di `rottaApertaAllOspite` (Hub, News).
                        Le altre non si montano affatto: al loro posto c'e' «Accedi», e sul
                        Profilo la scheda dell'ospite col nome che manda Telegram. */}
                    {ospite && !rottaApertaAllOspite(location.pathname) ? (
                      <RichiedeAccesso forma={onProfile ? 'profilo' : 'schermata'} />
                    ) : (
                      <Outlet />
                    )}
                  </main>
                  {/* L'azione persistente dei tornei Limulo (L-2195): SOPRA `MainCta`, cosi' il tasto
                      primario della schermata resta il piu' vicino al pollice, e nel flusso (non
                      fissa): lo scorrevole si accorcia invece di finirle sotto. Si prende l'inset
                      solo quando e' l'ultima cosa sullo schermo. ⚠️ Non convive con la barra della
                      scheda qui sotto: sulla scheda d'incontro non compare (v. il componente). */}
                  {/* Il provider vive SOLO qui (T2-5, disegno (b) di tma-orcr): la sfida in arrivo. */}
                  <CuoioDellaShell v3={v3} profilo={onProfile}>
                    <SfideBarraProvider>
                      <AzioneDellaShell v3={v3} insetBottom={!tabBarVisible} />
                    </SfideBarraProvider>
                  {/* La modale dell'esito (Luca, risposta a L-2484): legge la STESSA voce di cache delle azioni
                      della barra, senza un ritmo suo. Sta fuori dalla barra perche' la barra ha la sua PR. */}
                  <OsservatoreEsiti />
                  {/* Il posto della barra di una scheda (oggi il bottone unico dell'incontro, L-2196):
                      NEL FLUSSO sopra `MainCta`, cosi' non copre `ActiveMatchPill` e lo scorrevole si
                      accorcia da solo. V. `barraScheda.ts`. */}
                  <div id={BARRA_SCHEDA_ID} />
                  {/* PR 9c: con la v3 la partita viva SALE qui, nella stessa barra della barra del torneo (una barra sola), sopra `MainCta`; il fallback del `Suspense` e` la pastiglia di oggi. */}
                  {v3 && PartitaVivaV3 ? (
                    <Suspense fallback={<ActiveMatchPill />}>
                      <PartitaVivaV3 />
                    </Suspense>
                  ) : null}
                  </CuoioDellaShell>
                  <MainCta insetBottom={!tabBarVisible} />
                  {/* Anche queste due passano al cuoio sul Profilo. Non è
                      rifinitura: la pastiglia della partita in corso usa
                      `bg-brand/10`, e un accento al 10% sopra il marrone
                      desatura e diventa una STRISCIA SCURA sotto l'uovo — l'ha
                      segnalata CriGio come «barra nera, sembra un misuratore».
                      Non era un difetto suo: era il teal fuori dal suo ambiente. */}
                  <div className={cn('contents', onProfile && 'pack-scope')}>
                    <EngagementNudge />
                    {v3 && PartitaVivaV3 ? null : <ActiveMatchPill />}
                  </div>

                  {/* La linguetta pende dalla barra: sta ATTACCATA sopra di lei,
                      centrata sul tasto della Hub. Il contenitore non intercetta
                      i tocchi (`pointer-events-none`) perché è largo quanto lo
                      schermo e coprirebbe l'ultima riga di contenuto; solo la
                      linguetta li riprende. */}
                  {onProfile && !ospite && tabBarVisible && !packOpen && (
                    // Con la v3 le voci sono quattro e di uguale larghezza: il centro dello schermo cade fra Amici e Home. La linguetta si centra sulla voce HOME.
                    // Il centro di Home e' 0,625·L − 1,5 px (padding 8 e gap 4 della barra: 16 + 2,5·(L − 28)/4); il centro di un contenitore con un `padding-left` P e'
                    // 50% + P/2, quindi P = 25% − 3 px. Misurato a 360 e 390 dal banco `--chrome` (±1,2 px).
                    <div className="pointer-events-none relative z-10 flex justify-center" style={v3 ? { paddingLeft: 'calc(25% - 3px)' } : undefined}>
                      <PackTab
                        open={false}
                        onToggle={() => setPackOpen(true)}
                        label={t('pack.open', { defaultValue: 'Open backpack' })}
                        className="pointer-events-auto"
                      />
                    </div>
                  )}

                  {tabBarVisible && <BarraDellaShell v3={v3} />}

                  {onProfile && !ospite && <Backpack open={packOpen} onClose={() => setPackOpen(false)} />}

                  {/* ADR-0115 — L'UNICA modale di atterraggio, montata qui e non
                      per rotta. Sta dentro il ramo non-onboarding perche' la
                      «superficie viva» dev'essere davvero viva: sopra il wizard
                      di primo avvio non ci sarebbe niente sotto a cui restare, e
                      `usePendingInvite` aspetta comunque la fine dell'onboarding
                      prima di far partire un atterraggio. */}
                  <LandingNoticeModal />

                  {/* Il bottone flottante dei minigiochi (I-909, Luca L-3508 → (c); spostabile, I-991): l'UNICO punto di montaggio. `null` quando nessun gioco ha il
                      flag acceso (produzione); col flag acceso si disegna solo per chi ha `beta` e Limulo, dentro un ErrorBoundary locale: v. `GuscioProtetto`
                      e `lib/minigiochi.ts`. Sta DOPO la barra nel documento e a `z-20`, sotto ogni foglio (`z-50`). */}
                  <GuscioProtetto />
                  {/* Il Feedback della v3 (tma-orcr-98): prende il posto del pulsante dei minigiochi DENTRO la v3, dietro il flag letterale; fuori dalla v3 e in produzione rende null. */}
                  <GuscioFeedbackProtetto />
                </>
              )}
            </AdminGate>
          </div>
        </AppBannerProvider>
      </MainCtaProvider>
      </CodeTorneiProvider>
      </ResumableMatchProvider>
      </UscitaTavoloProvider>
    </ActiveMatchProvider>
    </LandingNoticeProvider>
  );
}
